import { useState, useEffect, useMemo } from "react";
import { ProductCard } from "./ProductCard";

interface ProductGridProduct {
  _id?: string;
  id?: string;
  [key: string]: unknown;
}

interface ProductGridTab {
  label: string;
  products?: ProductGridProduct[];
}

interface ProductGridSection {
  _id?: string;
  title?: string;
  subtitle?: string;
  tabs?: ProductGridTab[];
  products?: ProductGridProduct[];
}

interface ProductGridProps {
  section: ProductGridSection | null | undefined;
  mode?: "tabs" | "trending";
}

const MARQUEE_CARD_WIDTH = 224;
/** ~100s for 6 products (half of the old fixed 50s); scales up for longer tabs like All */
function getMarqueeDuration(itemCount: number): number {
  const secondsPerItem = 100 / 6;
  return Math.max(80, Math.round(Math.max(itemCount, 1) * secondsPerItem));
}

export function ProductGrid({ section, mode = "tabs" }: ProductGridProps) {
  const tabs = useMemo(() => {
    if (!section) return [];
    let t = section.tabs || [];
    if (!t.length && section.products?.length) {
      t = [{ label: "All", products: section.products }];
    }
    return t;
  }, [section]);

  const [activeTab, setActiveTab] = useState("All");

  useEffect(() => {
    if (tabs.length) {
      setActiveTab(tabs[0].label);
    }
  }, [section?._id, tabs]);

  if (!section || !tabs.length) return null;

  const activeTabData = tabs.find((t) => t.label === activeTab) || tabs[0];
  const items = activeTabData?.products || [];
  const isTrending = mode === "trending";
  const marqueeItems = [...items, ...items, ...items, ...items];

  if (isTrending) {
    const marqueeDuration = getMarqueeDuration(items.length);

    return (
      <section id="catalog" className="container mx-auto px-4 lg:px-8 pt-1.5 pb-2">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-2.5 mb-1.5">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-1">
              {section.subtitle || "Trending Now"}
            </p>
            <h2 className="text-xl sm:text-[1.65rem] lg:text-[1.75rem] font-display font-bold tracking-tight leading-tight">
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    section.title?.split(" ").slice(0, 2).join(" ") || "Shop the",
                }}
              />{" "}
              <span className="text-gradient-brand">
                {section.title?.split(" ").slice(2).join(" ") || "Hottest Drops"}
              </span>
            </h2>
          </div>

          {tabs.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1.5 overflow-x-auto rounded-xl scrollbar-hide">
                {tabs.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => setActiveTab(t.label)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      activeTab === t.label
                        ? "bg-gradient-brand text-primary-foreground shadow-glow-cyan"
                        : "bg-surface border border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {items.length > 4 ? (
          <div className="relative w-full overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
            <div
              className="flex w-max gap-4 animate-marquee hover:[animation-play-state:paused]"
              style={{ animationDuration: `${marqueeDuration}s` }}
            >
              {marqueeItems.map((p, idx) => (
                <div
                  key={`${p._id || p.id}-${idx}`}
                  className="shrink-0 flex flex-col"
                  style={{ width: MARQUEE_CARD_WIDTH }}
                >
                  <ProductCard p={p} variant="legacy" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-6 justify-start">
            {items.map((p) => (
              <div key={p._id || p.id} className="w-[280px] shrink-0 flex flex-col">
                <ProductCard p={p} variant="legacy" />
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="container mx-auto pt-12 pb-4 px-4 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          {section.subtitle && (
            <p className="text-xs sm:text-sm uppercase tracking-widest text-primary font-semibold mb-2">
              {section.subtitle}
            </p>
          )}
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-foreground">
            {section.title}
          </h2>
        </div>
        {tabs.length > 1 && (
          <div className="flex gap-2 overflow-x-auto rounded-xl scrollbar-hide">
            {tabs.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => setActiveTab(t.label)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === t.label
                    ? "bg-gradient-brand text-primary-foreground shadow-glow-cyan"
                    : "bg-surface border border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <ProductCard key={p._id || p.id} p={p} variant="cms" compact />
        ))}
      </div>
    </section>
  );
}
